import React, { useState } from "react";

import {
  IgButton,
  IgLink,
  IgSelect,
  IgTypography,
} from "@intelligems/ig-theme";
import type { ImagePosition, ImageScaling } from "@intelligems/ig-types";
import type { ImageComponent } from "@src/store/slices/experienceBuilder/types";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { ImageLibraryDialog } from "@src/components/Dialogs/ImageLibraryDialog";
import { URLInputDialog } from "@src/features/experienceBuilder/modifications/basicMods/URLInputDialog";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { useDialog } from "@src/hooks/dialog";
import { useDispatch, useSelector } from "@src/store";
import {
  ExperienceBuilderAction as Action,
  ExperienceBuilderSelector as Selector,
} from "@src/store/slices/experienceBuilder";
import { assert } from "@src/utils/assert";
import { ModificationHeader } from "@src/features/experienceBuilder/modifications/common/ModificationHeader";

const scalingOptions: { value: ImageScaling; label: string }[] = [
  { value: "contain", label: "Contain" },
  { value: "cover", label: "Cover" },
  { value: "fill", label: "Fill" },
  { value: "none", label: "None" },
  { value: "scale-down", label: "Scale down" },
];

const positionOptions: { value: ImagePosition; label: string }[] = [
  { value: "center", label: "Center" },
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom" },
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
  { value: "top left", label: "Top left" },
  { value: "top right", label: "Top right" },
  { value: "bottom left", label: "Bottom left" },
  { value: "bottom right", label: "Bottom right" },
];

export const ImageComponentPanel = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const imageLibraryDialog = useDialog("imageLibrary");
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);

  const mod = useSelector(Selector.component.getVariationDraft) as
    | ImageComponent
    | undefined;
  assert(mod !== undefined, "mod is undefined");
  const isApplicable = useSelector(Selector.isVariationDraftApplicable);

  const update = (changes: Partial<ImageComponent>) =>
    dispatch(Action.component.setDraft({ ...mod, ...changes }));
  const cancel = () => void dispatch(Action.cancelDraft({ trackCancel: true }));
  const apply = () => void dispatch(Action.applyDraft());

  const handleSelectImage = () => {
    imageLibraryDialog.open({
      id: imageLibraryDialog.id,
      onUpdateImage: (src: string) => update({ src }),
    });
  };

  return (
    <>
      <ModificationHeader />
      <ImageLibraryDialog id={imageLibraryDialog.id} />
      <URLInputDialog
        open={urlDialogOpen}
        onClose={() => setUrlDialogOpen(false)}
        onApply={(src) => update({ src })}
      />
      <Panel>
        <Panel.Body>
          <Panel.Header>Image</Panel.Header>
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={3}
            sx={{
              border: `1px solid ${theme.igBorders.core.default}`,
              borderRadius: "8px",
              backgroundColor:
                theme.igBackgroundColors.interactive.neutral.default,
              minHeight: 200,
              padding: 5,
            }}
          >
            {mod.src ? (
              <>
                <img
                  src={mod.src}
                  alt="Selected"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 140,
                    objectFit: "contain",
                    borderRadius: 4,
                  }}
                />
                <IgTypography
                  variant="subtitle1"
                  color="text.secondary"
                  noWrap
                  sx={{ maxWidth: "100%" }}
                >
                  {mod.src.split("/").pop()}
                </IgTypography>
                <IgButton
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleSelectImage}
                >
                  Change image
                </IgButton>
              </>
            ) : (
              <>
                <IgButton
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleSelectImage}
                >
                  Browse library
                </IgButton>
                <IgTypography variant="body1" color="text.secondary">
                  or{" "}
                  <IgLink
                    component="button"
                    onClick={() => setUrlDialogOpen(true)}
                    sx={{ verticalAlign: "baseline" }}
                  >
                    add an image URL
                  </IgLink>
                </IgTypography>
              </>
            )}
          </Stack>
          <Panel.TextField
            label="Alt text"
            value={mod.alt}
            onChange={(e) => update({ alt: e.target.value })}
            placeholder="Describe the image..."
          />
          <Grid container spacing={2}>
            <Grid size={6}>
              <Panel.TextField
                label="Width"
                value={mod.width}
                onChange={(e) => update({ width: e.target.value })}
                placeholder="100%"
              />
            </Grid>
            <Grid size={6}>
              <Panel.TextField
                label="Max height"
                value={mod.maxHeight}
                onChange={(e) => update({ maxHeight: e.target.value })}
                placeholder="none"
              />
            </Grid>
            <Grid size={6}>
              <IgSelect<ImageScaling>
                fullWidth
                size="small"
                label="Scaling"
                value={mod.scaling}
                onChange={(e) => update({ scaling: e.target.value })}
              >
                {scalingOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </IgSelect>
            </Grid>
            <Grid size={6}>
              <IgSelect<ImagePosition>
                fullWidth
                size="small"
                label="Position"
                value={mod.objectPosition}
                onChange={(e) => update({ objectPosition: e.target.value })}
              >
                {positionOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </IgSelect>
            </Grid>
          </Grid>
        </Panel.Body>

        <Panel.Buttons>
          <Panel.CancelButton onClick={cancel} />
          <Panel.ApplyButton onClick={apply} disabled={!isApplicable} />
        </Panel.Buttons>
      </Panel>
    </>
  );
};
